
/* 

  Just a wrapper for a reducer
  This was initially in App.jsx but it made the code too cluttered
  We just removed it out of App.jsx and abstracted away the reducer and its update functions
  For the sake of shortening App.jsx

*/


import React, { useReducer, useEffect, useContext, createContext } from 'react';

// Initial state for all tabs
const initialState = {
  // Common BMP selection parameters (shared across tabs)
  analysistype: "individual", // default analysistype
  bmptype: "",
  sitename: "",
  bmpname: "",
  selectedBmpType: "", // BMP type for the selected individual BMP
  
  // Tab-specific data
  wq: {
    selectedAnalytes: [], // Will contain objects with {analytename, threshold}
    indexData: {},
    emcOverTimeData: [],
    comparisonMode: false, // Add this line for toggling comparison mode
    scatterPlotMargin: { top: 30, right: 20, bottom: 40, left: 50 }
  },
  hydrology: {
    indexData: {},
    comparisonMode: false, // Add this line for toggling comparison mode in hydrology
    scatterPlotMargin: { top: 30, right: 20, bottom: 40, left: 50 }
  },
  // mpiData is an array of objects like:
  // [
  //   {
  //     analytename: "Nitrite as N, Total (mg/L)",
  //     priority: 1,
  //     score: 7.99,
  //     weight: 0.538,
  //     threshold: 0.5
  //   },
  //   {
  //     analytename: "Hydrology",
  //     priority: 1,
  //     score: 6.85,
  //     weight: 0.462,
  //     threshold: NaN
  //   }
  // ]
  mpiData: [], // Data for the Multi Parameter Index - gets updated as the user selects analytes, runs Hydrology analysis, etc
  mpiComparisonData: [], // Comparison data for MPI when comparison mode is enabled
  mpiComparisonMode: false, // Toggle for comparison mode in MPI tab
  
  // BMP type data from database
  bmpTypes: [] // Array of objects with bmptype and bmptypedescription
};

/*
   Reducer for managing state across all tabs
*/
function appReducer(state, action) {
  switch (action.type) {
    case 'UPDATE_BMP_STATE':
      return {
        ...state,
        ...action.payload
      };
    case 'UPDATE_WQ_STATE':
      return {
        ...state,
        wq: { ...state.wq, ...action.payload }
      };
    case 'UPDATE_HYDROLOGY_STATE':
      return {
        ...state,
        hydrology: { ...state.hydrology, ...action.payload }
      };
    case 'UPDATE_MULTI_PARAMETER_INDEX_DATA':
      return {
        ...state,
        mpiData: action.payload // Replace with new array
      };
    case 'UPDATE_MPI_COMPARISON_DATA':
      return {
        ...state,
        mpiComparisonData: action.payload // Replace with new array
      };
    case 'UPDATE_MPI_COMPARISON_MODE':
      return {
        ...state,
        mpiComparisonMode: action.payload
      };
    case 'CLEAR_MULTI_PARAMETER_INDEX_DATA':
        return {
            ...state,
            mpiData: [],
            mpiComparisonData: []
        };
    case 'UPDATE_WQ_INDEX_DATA':
        return {
            ...state,
            wq: { ...state.wq, indexData: { ...state.wq.indexData, ...action.payload } }
        };
    
    case 'UPDATE_WQ_EMC_OVER_TIME_DATA':
        return {
            ...state,
            wq: { ...state.wq, emcOverTimeData: action.payload }
        };
    
    case 'UPDATE_BMP_TYPES':
      return {
        ...state,
        bmpTypes: action.payload
      };
    case 'RESET_TAB_STATE':
      // Only reset if the tab exists in initialState
      if (initialState[action.tab]) {
        return {
          ...state,
          [action.tab]: initialState[action.tab]
        };
      }
      return state;
    default:
      return state;
  }
}

const AppStateContext = createContext();

export function AppStateProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  // Helper functions to dispatch actions for each tab
  const updateBmpState = (updates) => {
    dispatch({ type: 'UPDATE_BMP_STATE', payload: updates });
  };

  const updateWqState = (updates) => {
    dispatch({ type: 'UPDATE_WQ_STATE', payload: updates });
  };

  const updateHydrologyState = (updates) => {
    dispatch({ type: 'UPDATE_HYDROLOGY_STATE', payload: updates });
  };

  const clearMultiParameterIndexData = () => {
    dispatch({ type: 'CLEAR_MULTI_PARAMETER_INDEX_DATA' });
  };
  
  const updateWQIndexData = (updates) => {
    dispatch({ type: 'UPDATE_WQ_INDEX_DATA', payload: updates });
  };
  
  const updateEMCOverTimeData = (updates) => {
    dispatch({ type: 'UPDATE_WQ_EMC_OVER_TIME_DATA', payload: updates });
  };

  const updateMpiData = (updatedMpiData) => {
    dispatch({ type: 'UPDATE_MULTI_PARAMETER_INDEX_DATA', payload: updatedMpiData });
  };

  const updateMpiComparisonData = (updatedMpiComparisonData) => {
    dispatch({ type: 'UPDATE_MPI_COMPARISON_DATA', payload: updatedMpiComparisonData });
  };

  const updateMpiComparisonMode = (comparisonMode) => {
    dispatch({ type: 'UPDATE_MPI_COMPARISON_MODE', payload: comparisonMode });
  };

  const updateBmpTypes = (bmpTypes) => {
    dispatch({ type: 'UPDATE_BMP_TYPES', payload: bmpTypes });
  };

  // Extract BMP state for passing to components
  const bmpState = {
    analysistype: state.analysistype,
    bmptype: state.bmptype,
    sitename: state.sitename,
    bmpname: state.bmpname,
    selectedBmpType: state.selectedBmpType
  };

  // Helper function to get BMP type display name from database
  const getBmpTypeDisplayName = (bmptypeValue) => {
    const bmpTypeObj = state.bmpTypes.find(bmp => bmp.bmptype === bmptypeValue);
    return bmpTypeObj?.bmptypedescription || bmptypeValue;
  };

  // Clear previous MPI data when the BMP selection changes
  useEffect(() => {
    clearMultiParameterIndexData();
    updateMpiComparisonMode(false); // Also clear comparison mode
    updateWqState({ indexData: {}, selectedAnalytes: [] });
    updateHydrologyState({ indexData: {}, selectedAnalytes: [] });
  }, [state.analysistype, state.bmptype, state.sitename, state.bmpname]);

  // Fetch BMP types from database on component mount
  useEffect(() => {
    const fetchBmpTypes = async () => {
      try {
        const API_BASE = process.env.API_BASE ?? "";
        const response = await fetch(`${API_BASE}/selectioncontrols/wq/bmptype/bmptypes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: "{}"
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: Failed to fetch BMP types`);
        }
        
        const data = await response.json();
        updateBmpTypes(data.bmptypes || []);
      } catch (error) {
        updateBmpTypes([]);
      }
    };
    fetchBmpTypes();
  }, []);

  // Helper function to get display label
  const getDisplayLabel = (value, labelMap) => {
    return labelMap[value] || value;
  };

  // Get BMP display name for selected BMP
  const getSelectedBmpLabel = () => {
    if (state.analysistype === 'individual' && state.sitename && state.bmpname) {
      return `${state.sitename}: ${state.bmpname}`;
    } else if (state.analysistype === 'bmptype' && state.bmptype) {
      return state.bmptype;
    } else if (state.analysistype === 'treatmenttrain' && state.sitename) {
      return state.sitename;
    }
    return 'Selected BMP';
  };

  // Label mappings for display (move to context)
  const ANALYSIS_TYPE_LABELS = {
    'bmptype': 'BMP Type',
    'individual': 'Individual BMP',
    'treatmenttrain': 'Treatment Train'
  };

  const contextValue = {
    state,
    bmpState,
    updateBmpState,
    updateWqState,
    updateHydrologyState,
    clearMultiParameterIndexData,
    updateWQIndexData,
    updateEMCOverTimeData,
    updateMpiData,
    updateMpiComparisonData,
    updateMpiComparisonMode,
    updateBmpTypes,
    getBmpTypeDisplayName,
    getDisplayLabel,
    getSelectedBmpLabel,
    ANALYSIS_TYPE_LABELS
  };

  return (
    <AppStateContext.Provider value={contextValue}>
      {children}
    </AppStateContext.Provider>
  );
}

export function useAppStateContext() {
  const ctx = useContext(AppStateContext);
  if (!ctx) throw new Error('useAppStateContext must be used within an AppStateProvider');
  return ctx;
}

// Keep the old hook for migration, but recommend using context
export { initialState, appReducer }; 